/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import {
    CacheType,
    createObservableDataAction,
    IAction,
    IActionContext,
    IActionInput,
    ICreateActionContext
} from '@msdyn365-commerce/core';

import { getDocumentAvailabilityAsync } from './DataService/DataActionExtension.g';
import { IGetDocumentAvailabilityResponse } from './DataService/DataServiceEntities.g';
import { DocumentTypeInvoice } from './get-document-download-url.action';

/**
 * GetDocumentAvailability Input Action.
 * Le numéro de compte client n'est pas transmis : il est résolu côté CSU depuis la
 * session authentifiée.
 */
export class GetDocumentAvailabilityInput implements IActionInput {
    public documentType: string;
    public documentId: string;

    constructor(documentId: string, documentType: string = DocumentTypeInvoice) {
        this.documentId = documentId;
        this.documentType = documentType;
    }

    public getCacheKey = () => `GetDocumentAvailability-${this.documentType}-${this.documentId}`;
    public getCacheObjectType = () => 'GetDocumentAvailabilityResponse';

    // Aucune mise en cache : un document devient disponible dès que l'agent confirme la
    // commande, une réponse recyclée garderait le bouton grisé après coup.
    public dataCacheType = (): CacheType => 'none';
}

/**
 * Indique si le PDF d'un document est déjà téléchargeable.
 * L'action ne lève pas d'exception : un document indisponible est un cas normal,
 * l'appelant se fie uniquement à Success et Available.
 */
export async function getDocumentAvailability(
    input: GetDocumentAvailabilityInput,
    ctx: IActionContext
): Promise<IGetDocumentAvailabilityResponse> {
    if (!input.documentId) {
        return { Success: false, Status: 'Error', Available: false, ErrorMessage: 'Document id is required' };
    }

    return getDocumentAvailabilityAsync({ callerContext: ctx }, input.documentType, input.documentId)
        .then(data => {
            return data;
        })
        .catch(error => {
            ctx.telemetry.exception(error);
            ctx.trace(`[get-document-availability] Unable to get the availability of document ${input.documentId}`);

            return { Success: false, Status: 'Error', Available: false, ErrorMessage: 'Unable to get the document availability' };
        });
}

const createInput = (args: ICreateActionContext, documentId: string, documentType?: string): IActionInput => {
    return new GetDocumentAvailabilityInput(documentId, documentType);
};

export default createObservableDataAction({
    action: <IAction<IGetDocumentAvailabilityResponse>>getDocumentAvailability,
    id: 'GetDocumentAvailability',
    input: createInput
});
